import { Box, Button, CircularProgress, Typography } from '@mui/material';
import React, { useState } from 'react';
import AddFootnoteToolbarButton from './newContentManagement/AddFootnoteToolbarButton';
import BlockDropZone from './newContentManagement/BlockDropZone';
import BlockPalette from './newContentManagement/BlockPalette';
import { BlockPaletteDragProvider } from './newContentManagement/blockPaletteDragContext';
import ContentBlockEditor from './newContentManagement/ContentBlockEditor';
import ContentBlockWrapper from './newContentManagement/ContentBlockWrapper';
import ContentEditorPreviewPanels from './newContentManagement/ContentEditorPreviewPanels';
import ContentEditorToolbar from './newContentManagement/ContentEditorToolbar';
import { EditorSidebarProvider } from './newContentManagement/editorSidebarContext';
import {
  blockSupportsFootnotes,
  createContentBlock,
  isFootnotesOpen,
} from './newContentManagement/newContentManagementEditorUtils';
import { useNewContentManagementEditor } from './newContentManagement/useNewContentManagementEditor';
import { useStaticPdfUpload } from './newContentManagement/useStaticPdfUpload';
import LibraryPanel from './newContentManagement/LibraryPanel';

export default function NewContentManagementEditor() {
  const editor = useNewContentManagementEditor();
  const staticPdf = useStaticPdfUpload(editor.fundOpportunity, editor.backendService);
  // Set by a type-mismatch confirm that swapped a block to empty Image
  // Cards — tells that one freshly-mounted block (by index) to auto-run the
  // file through its own paste pipeline once, via ContentBlockEditor's
  // autoImportFile prop.
  const [pendingImageImport, setPendingImageImport] = useState<{
    index: number;
    file: File;
  } | null>(null);

  if (editor.loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={400} gap={2}>
        <CircularProgress size={24} />
        <Typography>Loading…</Typography>
      </Box>
    );
  }

  if (editor.error) {
    return (
      <Box p={3}>
        <Typography color="error">{editor.error}</Typography>
        <Button onClick={editor.goBack} sx={{ mt: 1 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!editor.content || !editor.fundOpportunity) return null;

  const blocks = editor.content.content;

  return (
    <BlockPaletteDragProvider>
      <EditorSidebarProvider onImportSections={editor.appendImportedSections}>
        <Box sx={{ width: '100%' }}>
          <ContentEditorToolbar
            fundOpportunity={editor.fundOpportunity}
            saveStatus={editor.saveStatus}
            pageMode={editor.pageMode}
            toolbarRef={editor.toolbarRef}
            onBack={editor.goBack}
            onPageModeChange={editor.setPageMode}
            onSaveNow={editor.saveNow}
            onPublish={editor.publishNow}
            published={!editor.canPublish}
            publishDisabled={
              !editor.canPublish ||
              editor.publishing ||
              editor.saveStatus === 'saving' ||
              editor.saveStatus === 'pending'
            }
            onRevert={editor.revertToPublished}
            reverting={editor.reverting}
            revertDisabled={
              !editor.canPublish ||
              editor.reverting ||
              editor.publishing ||
              editor.saveStatus === 'saving' ||
              editor.saveStatus === 'pending'
            }
            onUploadPdf={staticPdf.openPicker}
            pdfUploading={staticPdf.uploading}
          />
          <input
            ref={staticPdf.inputRef}
            type="file"
            accept="application/pdf"
            hidden
            onChange={(event) => void staticPdf.onFileSelected(event.target.files?.[0])}
          />

          {editor.pageMode === 'library' ? (
            <LibraryPanel
              fundOpportunityId={editor.content.relatedObjectId}
              backendService={editor.backendService}
            />
          ) : editor.pageMode !== 'edit' ? (
            <ContentEditorPreviewPanels
              pageMode={editor.pageMode}
              content={editor.content}
              fundOpportunity={editor.fundOpportunity}
              previewInvestor={editor.previewInvestor}
            />
          ) : (
            <>
              <BlockPalette
                emptyCanvas={blocks.length === 0}
                fundOpportunityId={editor.content.relatedObjectId}
                backendService={editor.backendService}
              />
              <Box>
                <BlockDropZone
                  index={-1}
                  where="after"
                  onDrop={editor.addBlock}
                  emptyState
                  smartPaste={{
                    modelId: editor.content.relatedObjectId,
                    modelType: 'FundOpportunity',
                    backendService: editor.backendService,
                    onResolved: (sections, cropMeta) =>
                      editor.appendImportedSections(sections, cropMeta, 0),
                  }}
                />
                {blocks.map((block, index) => {
                  const footnotesOpen = isFootnotesOpen(block, editor.footnotesOpenByBlockId);
                  return (
                    <React.Fragment key={block.id}>
                      <BlockDropZone index={index} where="before" onDrop={editor.addBlock} />
                      <ContentBlockWrapper
                        index={index}
                        total={blocks.length}
                        blockType={block.type}
                        blockId={block.id}
                        onMove={editor.moveBlock}
                        onRemove={editor.removeBlock}
                        onAdd={editor.addBlock}
                        hideInsertSlots
                        highlighted={editor.recentlyInsertedIds.includes(block.id)}
                        highlightLead={block.id === editor.recentlyInsertedIds[0]}
                        toolbarAction={
                          blockSupportsFootnotes(block.type) ? (
                            <AddFootnoteToolbarButton
                              open={footnotesOpen}
                              onClick={() => editor.toggleFootnotes(block.id, footnotesOpen)}
                            />
                          ) : undefined
                        }
                      >
                        <ContentBlockEditor
                          block={block}
                          onChange={(updated) => editor.updateBlock(index, updated)}
                          modelId={editor.content!.relatedObjectId}
                          backendService={editor.backendService}
                          onInsertSections={(sections) => editor.insertSections(index, sections)}
                          onSwapToImageCards={(file) => {
                            editor.insertSections(index, [
                              createContentBlock('ImageCarousel', editor.content!.relatedObjectId),
                            ]);
                            setPendingImageImport({ index, file });
                          }}
                          autoImportFile={
                            pendingImageImport?.index === index
                              ? pendingImageImport.file
                              : undefined
                          }
                          onAutoImportConsumed={() => setPendingImageImport(null)}
                          primeCardsAdjust={editor.cardsAdjustByBlockId[block.id]}
                          onPrimeCardsAdjustConsumed={() =>
                            editor.clearCardsAdjustForBlock(block.id)
                          }
                          footnotesOpen={footnotesOpen}
                        />
                      </ContentBlockWrapper>
                    </React.Fragment>
                  );
                })}
                {blocks.length > 0 && (
                  <BlockDropZone index={blocks.length - 1} where="after" onDrop={editor.addBlock} />
                )}
              </Box>
            </>
          )}
        </Box>
      </EditorSidebarProvider>
    </BlockPaletteDragProvider>
  );
}
